// Legal Pages Component
// Privacy Policy, Terms of Service, Compliance, and Disclaimer Pages
// Last Updated: October 2025

import React from 'react';

// Privacy Policy Component
export const PrivacyPolicy = () => {
    // Scroll to top when component mounts
    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
            
            <div className="prose prose-blue max-w-none">
                <p className="text-sm text-gray-600 mb-6">
                    <strong>Effective Date:</strong> October 13, 2025<br/>
                    <strong>Last Updated:</strong> January 15, 2025<br/>
                    <strong>Version:</strong> 1.1 - <a href="#changelog" className="text-blue-600 hover:underline">View Changelog</a>
                </p>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
                This Privacy Policy explains how SuperCalc Pro ("we", "our", "the Calculator") 
                collects, uses, and protects any information when you use our service.
            </p>
                    <p className="text-gray-700">
                        We are committed to ensuring that your privacy is protected. We do not collect, store, or transmit 
                        personal financial information to any server.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Information We Collect</h2>
                    
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Local Data Storage</h3>
                    <p className="text-gray-700 mb-4">
                        All calculation data (age, superannuation balance, income, etc.) is stored <strong>locally on your device only</strong> 
                        using browser LocalStorage. This data:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li>Remains on your device</li>
                        <li>Is never transmitted to our servers</li>
                        <li>Can be deleted at any time using the "Clear Data" button</li>
                        <li>Is automatically cleared when you clear your browser data</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Analytics Data (Anonymous)</h3>
                    <p className="text-gray-700 mb-4">
                        We collect anonymized usage statistics to improve the calculator:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li>Which calculators are used most frequently</li>
                        <li>Which features are accessed</li>
                        <li>Error reports (no personal data included)</li>
                        <li>Page load times and performance metrics</li>
                        <li>Browser type and device type (for compatibility)</li>
                    </ul>
                    <p className="text-gray-700 mb-4">
                        <strong>We do NOT collect:</strong>
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li>Your name, email, or contact information (except when voluntarily submitted for free run unlocks)</li>
                        <li>Your actual superannuation balance or financial data</li>
                        <li>Your age, income, or personal circumstances</li>
                        <li>Your precise location</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 Free Run Usage Tracking</h3>
                    <p className="text-gray-700 mb-4">
                        To ensure fair usage of our free calculator tools, we track:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li><strong>IP address:</strong> Used to count free calculator runs per user/device</li>
                        <li><strong>Email address:</strong> Voluntarily collected when you request additional free runs</li>
                        <li><strong>Run count:</strong> Number of calculations performed (stored locally in your browser)</li>
                    </ul>
                    <p className="text-gray-700 mb-4">
                        Under the 2026 Australian Privacy Act reforms, IP addresses are classified as Personal Identifiable Information (PII). 
                        We use IP monitoring <strong>solely to ensure fair usage</strong> of our free tools and prevent abuse. Your IP address 
                        is <strong>never sold, shared, or used for marketing</strong>.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. How We Use Information</h2>
                    <p className="text-gray-700 mb-4">
                        The anonymized analytics data is used solely to:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li>Improve calculator accuracy and functionality</li>
                        <li>Identify and fix bugs</li>
                        <li>Understand which features users find valuable</li>
                        <li>Optimize performance and load times</li>
                        <li>Ensure browser compatibility</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Data Sharing</h2>
                    <p className="text-gray-700 mb-4">
                        <strong>We never share, sell, or transmit your data.</strong>
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li>No third-party access to your calculations</li>
                        <li>No marketing partners receive your information</li>
                        <li>No data brokers have access to your usage</li>
                        <li>Anonymous analytics may be processed by Google Analytics (if enabled)</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Cookies and Tracking</h2>
                    <p className="text-gray-700 mb-4">
                        We use minimal cookies and tracking:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li><strong>Essential cookies:</strong> Session management, disclaimer acceptance</li>
                        <li><strong>Analytics cookies:</strong> Anonymous usage statistics (can be disabled)</li>
                        <li><strong>No advertising cookies:</strong> We do not serve ads or track for advertising</li>
                    </ul>
                    
                    <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400 mb-4">
                        <h3 className="text-lg font-semibold text-yellow-900 mb-2">Cookie Management</h3>
                        <p className="text-yellow-800 mb-2">
                            You can control cookie settings:
                        </p>
                        <ul className="list-disc list-inside text-yellow-800 space-y-1 ml-4">
                            <li><strong>Browser Settings:</strong> Disable cookies in your browser preferences</li>
                            <li><strong>Analytics Opt-Out:</strong> <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Analytics Opt-Out</a></li>
                            <li><strong>Do Not Track:</strong> Enable "Do Not Track" in your browser settings</li>
                        </ul>
                        <p className="text-yellow-800 mt-2 text-sm">
                            Note: Disabling analytics cookies will not affect calculator functionality.
                        </p>
                    </div>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Your Rights</h2>
                    <p className="text-gray-700 mb-4">You have the right to:</p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li><strong>Delete your data:</strong> Use the "Clear Data" button in any calculator</li>
                        <li><strong>Opt out of analytics:</strong> Use browser tracking prevention or contact us</li>
                        <li><strong>Request information:</strong> Contact us to ask what anonymous data we've collected</li>
                        <li><strong>Data portability:</strong> Export your scenarios using the PDF export feature</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Data Security</h2>
                    <p className="text-gray-700 mb-4">
                        We implement security measures to protect your privacy:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li>All data stays on your device (no server transmission)</li>
                        <li>HTTPS encryption for website access</li>
                        <li>No accounts or passwords required (no data breach risk)</li>
                        <li>Regular security updates to our codebase</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Children's Privacy</h2>
                    <p className="text-gray-700">
                        This calculator is not intended for use by individuals under 18 years of age. 
                        We do not knowingly collect data from children.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Changes to This Policy</h2>
                    <p className="text-gray-700 mb-4">
                        We may update this privacy policy from time to time. Changes will be posted on this page with 
                        an updated "Last Updated" date.
                    </p>
                    <p className="text-gray-700">
                        Continued use of the calculator after changes constitutes acceptance of the updated policy.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Contact Us</h2>
                    <p className="text-gray-700 mb-4">
                        If you have questions about this Privacy Policy, please contact us at:
                    </p>
                    <p className="text-gray-700">
                        <strong>Email:</strong> support@supercalcpro.com.au
                    </p>
                </section>

                <div className="bg-blue-50 p-6 rounded-lg mt-8">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Summary</h3>
                    <p className="text-blue-800">
                        <strong>Your privacy is paramount:</strong> All your financial data stays on your device. 
                        We only collect anonymous usage statistics to improve the calculator. We never sell or share your information.
                    </p>
                </div>

                <section id="changelog" className="mb-8 mt-12">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">Privacy Policy Changelog</h2>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Version 1.1 (January 15, 2025)</h3>
                        <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                            <li>Added explicit cookie management options</li>
                            <li>Enhanced analytics opt-out mechanisms</li>
                            <li>Clarified Google Analytics data processing</li>
                            <li>Added Do Not Track browser support</li>
                        </ul>
                        
                        <h3 className="text-lg font-semibold text-gray-800 mb-2 mt-4">Version 1.0 (October 13, 2025)</h3>
                        <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                            <li>Initial privacy policy release</li>
                            <li>LocalStorage-only data retention policy</li>
                            <li>Anonymous analytics implementation</li>
                            <li>Australian Privacy Principles compliance</li>
                        </ul>
                    </div>
                </section>
            </div>
        </div>
    );
};

// Terms of Service Component
export const TermsOfService = () => {
    // Scroll to top when component mounts
    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Terms of Service</h1>
            
            <div className="prose prose-blue max-w-none">
                <p className="text-sm text-gray-600 mb-6">
                    <strong>Effective Date:</strong> October 13, 2025<br/>
                    <strong>Last Updated:</strong> October 13, 2025
                </p>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-4">
                By accessing and using SuperCalc Pro ("the Calculator"), you accept and agree 
                to be bound by these Terms of Service and our Privacy Policy.
            </p>
                    <p className="text-gray-700">
                        If you do not agree to these terms, please do not use the Calculator.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Description of Service</h2>
                    <p className="text-gray-700 mb-4">
                        The Calculator is an <strong>educational tool</strong> that provides:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li>Retirement income projections</li>
                        <li>Superannuation accumulation estimates</li>
                        <li>Age Pension eligibility calculations</li>
                        <li>SMSF (Self-Managed Super Fund) compliance tools</li>
                        <li>Financial scenario modeling and comparisons</li>
                    </ul>
                    <p className="text-gray-700">
                        The Calculator is provided <strong>for informational and educational purposes only</strong>.
                    </p>
                </section>

                <section className="mb-8 bg-yellow-50 p-6 rounded-lg border-2 border-yellow-200">
                    <h2 className="text-2xl font-semibold text-red-800 mb-4">3. NOT FINANCIAL ADVICE - CRITICAL DISCLAIMER</h2>
                    <p className="text-red-700 mb-4 font-semibold text-lg">
                        THE CALCULATOR DOES NOT PROVIDE FINANCIAL, LEGAL, TAX, OR INVESTMENT ADVICE.
                    </p>
                    <p className="text-gray-800 mb-4">
                        You acknowledge and agree that:
                    </p>
                    <ul className="list-disc list-inside text-gray-800 mb-4 ml-4">
                        <li>This is an educational tool, not professional financial advice</li>
                        <li>Results are estimates based on assumptions that may not reflect reality</li>
                        <li>You should consult qualified financial advisers before making any financial decisions</li>
                        <li>We do not consider your personal circumstances, objectives, or financial situation</li>
                        <li>Past performance (historical data) does not guarantee future results</li>
                        <li>Investment returns, inflation, and economic factors vary significantly</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. License and Usage</h2>
                    <p className="text-gray-700 mb-4">
                        We grant you a limited, non-exclusive, non-transferable license to use the Calculator for:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li><strong>Personal use:</strong> Your own retirement planning and education</li>
                        <li><strong>Educational use:</strong> Learning about superannuation and retirement</li>
                    </ul>
                    <p className="text-gray-700 mb-4">
                        <strong>You may NOT:</strong>
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li>Use the Calculator to provide financial advice to others commercially</li>
                        <li>Redistribute, resell, or sublicense the Calculator</li>
                        <li>Reverse engineer, decompile, or extract source code</li>
                        <li>Remove copyright notices or branding</li>
                        <li>Use the Calculator for any illegal purposes</li>
                        <li>Claim the Calculator's outputs as your own professional advice</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Accuracy and Limitations</h2>
                    <p className="text-gray-700 mb-4">
                        While we strive for accuracy, you acknowledge that:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li>Calculations are based on assumptions that may not match reality</li>
                        <li>Tax rates, contribution caps, and Age Pension rules change frequently</li>
                        <li>We update rates regularly but cannot guarantee real-time accuracy</li>
                        <li>Investment returns are projections, not guarantees</li>
                        <li>Your actual results will differ from estimates</li>
                        <li>Complex personal circumstances may not be fully captured</li>
                    </ul>
                    <p className="text-gray-700 font-semibold">
                        Always verify critical information with official sources (ATO, Services Australia, qualified advisers).
                    </p>
                </section>

                <section className="mb-8 bg-red-50 p-6 rounded-lg border-2 border-red-200">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Limitation of Liability</h2>
                    <p className="text-gray-800 mb-4 font-semibold">
                        TO THE MAXIMUM EXTENT PERMITTED BY LAW:
                    </p>
                    <ul className="list-disc list-inside text-gray-800 mb-4 ml-4">
                        <li>We provide the Calculator "AS IS" without warranties of any kind</li>
                        <li>We are NOT liable for any financial losses, lost profits, or damages arising from use</li>
                        <li>We are NOT liable for errors, omissions, or inaccuracies in calculations</li>
                        <li>We are NOT liable for decisions you make based on the Calculator's output</li>
                        <li>We are NOT liable for service interruptions, bugs, or technical issues</li>
                        <li>Your use of the Calculator is entirely at your own risk</li>
                    </ul>
                    <p className="text-gray-800 font-semibold">
                        Some jurisdictions do not allow limitation of liability. In such cases, our liability is limited to the fullest extent permitted by law.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Indemnification</h2>
                    <p className="text-gray-700">
                        You agree to indemnify and hold harmless the Calculator, its creators, and affiliates from any claims, 
                        damages, losses, or expenses arising from:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li>Your use of the Calculator</li>
                        <li>Your violation of these Terms</li>
                        <li>Your violation of any applicable laws or regulations</li>
                        <li>Financial decisions made based on Calculator outputs</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Intellectual Property</h2>
                    <p className="text-gray-700 mb-4">
                        All content, design, code, and functionality of the Calculator is protected by copyright and intellectual property laws.
                    </p>
                    <p className="text-gray-700">
                        © 2026 Superannuation Calculator Suite. All rights reserved.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Data and Privacy</h2>
                    <p className="text-gray-700 mb-4">
                        Your use of the Calculator is also governed by our Privacy Policy. Key points:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li>All calculation data stays on your device (no server storage)</li>
                        <li>We collect anonymous usage statistics only</li>
                        <li>We never sell or share your information</li>
                        <li>You can delete your data at any time</li>
                    </ul>
                </section>

                <section className="mb-8 bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">9.1 Free Run Usage and IP Monitoring</h2>
                    <p className="text-gray-700 mb-4">
                        To ensure fair usage of our free calculator tools, we implement the following:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li><strong>IP Address Monitoring:</strong> We track your IP address to count free calculator runs per user/device</li>
                        <li><strong>Email Collection:</strong> When you voluntarily request additional free runs, we collect your email address</li>
                        <li><strong>Fair Usage Policy:</strong> Free run limits apply per IP address to prevent abuse</li>
                    </ul>
                    <p className="text-gray-700 mb-4">
                        Under the 2026 Australian Privacy Act reforms, IP addresses are classified as Personal Identifiable Information (PII). 
                        We use IP monitoring <strong>solely to ensure fair usage</strong> of our free tools. Your IP address is:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li><strong>Never sold</strong> to third parties</li>
                        <li><strong>Never used</strong> for marketing or advertising</li>
                        <li><strong>Never shared</strong> except as required by law</li>
                        <li><strong>Stored securely</strong> and deleted after run counting</li>
                    </ul>
                    <p className="text-gray-700">
                        By using the free tools, you agree to this IP monitoring for fair usage purposes.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Service Modifications</h2>
                    <p className="text-gray-700">
                        We reserve the right to:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                        <li>Modify, suspend, or discontinue the Calculator at any time</li>
                        <li>Update these Terms of Service (with notice)</li>
                        <li>Change features, functionality, or availability</li>
                        <li>Refuse service to anyone for any reason</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">11. Governing Law</h2>
                    <p className="text-gray-700 mb-4">
                        These Terms are governed by the laws of Australia. Any disputes shall be resolved in the courts of Western Australia.
                    </p>
                    <p className="text-gray-700 text-sm">
                        For the avoidance of doubt, this includes all matters relating to the interpretation, validity, and enforcement of these Terms.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">12. Contact</h2>
                    <p className="text-gray-700">
                        For questions about these Terms, contact us at: <strong>support@supercalcpro.com.au</strong>
                    </p>
                </section>

                <div className="bg-blue-50 p-6 rounded-lg mt-8">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Quick Summary</h3>
                    <p className="text-blue-800">
                        This is an educational tool, not financial advice. Use it to learn and explore scenarios, 
                        but always consult qualified professionals before making financial decisions. We're not liable 
                        for decisions you make based on these calculations.
                    </p>
                </div>
            </div>
        </div>
    );
};

// Disclaimer Page Component
export const DisclaimerPage = () => {
    // Scroll to top when component mounts
    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Important Disclaimer</h1>
            
            <div className="prose prose-blue max-w-none">
                <div className="bg-red-50 p-6 rounded-lg border-2 border-red-300 mb-8">
                    <h2 className="text-2xl font-bold text-red-900 mb-4">⚠️ READ THIS CAREFULLY</h2>
                    <p className="text-red-800 font-semibold text-lg">
                        This calculator is for EDUCATIONAL PURPOSES ONLY. It is NOT financial advice.
                    </p>
                </div>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Not Financial Advice</h2>
                    <p className="text-gray-700 mb-4">
                        SuperCalc Pro is an educational tool designed to help you understand retirement planning concepts. 
                        It is <strong>NOT</strong>:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li>Financial advice or recommendations</li>
                        <li>Tax advice or tax planning</li>
                        <li>Legal advice</li>
                        <li>Investment advice</li>
                        <li>A substitute for professional financial planning</li>
                    </ul>
                    <p className="text-gray-700 font-semibold text-lg">
                        You MUST seek advice from qualified, licensed financial professionals before making any financial decisions.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. No Personal Advice</h2>
                    <p className="text-gray-700 mb-4">
                        This calculator does NOT consider:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li>Your personal financial situation</li>
                        <li>Your specific needs and objectives</li>
                        <li>Your risk tolerance</li>
                        <li>Your complete financial picture (debts, assets, insurance)</li>
                        <li>Your health, family circumstances, or estate planning needs</li>
                        <li>Complex tax situations or structures</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Accuracy Limitations</h2>
                    <p className="text-gray-700 mb-4">
                        While we strive for accuracy:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li><strong>Rates change:</strong> Tax rates, contribution caps, and Age Pension rules are updated regularly by the government. We update our data frequently but cannot guarantee real-time accuracy.</li>
                        <li><strong>Assumptions required:</strong> Calculations require assumptions about investment returns, inflation, life expectancy, and other variables that cannot be predicted with certainty.</li>
                        <li><strong>Simplified models:</strong> Complex financial situations may not be fully captured by the calculator's models.</li>
                        <li><strong>Past performance:</strong> Historical data does not guarantee future results. Markets fluctuate unpredictably.</li>
                    </ul>
                    <p className="text-gray-700 font-semibold">
                        Always verify critical information with official sources (ATO, Services Australia, your super fund).
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. No Guarantees</h2>
                    <p className="text-gray-700 mb-4">
                        We make NO guarantees regarding:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li>Accuracy, completeness, or reliability of calculations</li>
                        <li>Achievement of any projected outcomes</li>
                        <li>Investment performance or returns</li>
                        <li>Age Pension eligibility or amounts</li>
                        <li>Tax outcomes or SMSF compliance</li>
                        <li>Suitability for your particular situation</li>
                    </ul>
                </section>

                <section className="mb-8 bg-orange-50 p-6 rounded-lg border-2 border-orange-300">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Limitation of Liability</h2>
                    <p className="text-gray-800 mb-4 font-semibold">
                        WE ARE NOT LIABLE FOR:
                    </p>
                    <ul className="list-disc list-inside text-gray-800 mb-4 ml-4 space-y-2">
                        <li>Financial losses or damages arising from use of this calculator</li>
                        <li>Decisions made based on calculator outputs</li>
                        <li>Errors, omissions, or inaccuracies in calculations</li>
                        <li>Changes in laws, rates, or regulations not yet reflected in the calculator</li>
                        <li>Technical issues, bugs, or service interruptions</li>
                        <li>Consequential, indirect, or special damages of any kind</li>
                    </ul>
                    <p className="text-gray-800 font-semibold text-lg mt-4">
                        USE AT YOUR OWN RISK.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. When to Seek Professional Advice</h2>
                    <p className="text-gray-700 mb-4">
                        You SHOULD seek professional financial advice if:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li>You have significant superannuation balances ($500k+)</li>
                        <li>You're considering starting an SMSF</li>
                        <li>You're nearing retirement or transitioning to retirement</li>
                        <li>You have complex tax situations</li>
                        <li>You're planning major contributions or withdrawals</li>
                        <li>You need Centrelink/Age Pension optimization</li>
                        <li>You're dealing with estate planning or death benefits</li>
                        <li>You have any doubts about your financial decisions</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. SMSF-Specific Disclaimer</h2>
                    <p className="text-gray-700 mb-4">
                        The SMSF tools are for educational purposes only. They do NOT:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li>Constitute advice on whether you should start an SMSF</li>
                        <li>Replace professional SMSF administration, accounting, or audit services</li>
                        <li>Guarantee compliance with SMSF regulations</li>
                        <li>Provide legal or tax advice for SMSF structures</li>
                    </ul>
                    <p className="text-gray-700 font-semibold">
                        SMSFs are complex and heavily regulated. Always engage qualified SMSF professionals.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Information Currency</h2>
                    <p className="text-gray-700 mb-4">
                        Financial regulations change frequently. This calculator uses:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li><strong>2025-26 financial year rates</strong> (as of October 2025)</li>
                        <li>Current contribution caps, tax rates, and Age Pension thresholds</li>
                        <li>Latest ATO and Services Australia published rates</li>
                    </ul>
                    <p className="text-gray-700 font-semibold">
                        Always check official government websites for the most current information before making decisions.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Educational Purpose</h2>
                    <p className="text-gray-700 mb-4">
                        This calculator is designed to help you:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li><strong>Learn:</strong> Understand retirement planning concepts</li>
                        <li><strong>Explore:</strong> Test different scenarios and strategies</li>
                        <li><strong>Estimate:</strong> Get ballpark figures for planning discussions</li>
                        <li><strong>Prepare:</strong> Come to financial adviser meetings better informed</li>
                    </ul>
                    <p className="text-gray-700 font-semibold">
                        It is NOT designed to replace professional financial planning.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">10. Your Acknowledgment</h2>
                    <p className="text-gray-700 mb-4">
                        By using this calculator, you acknowledge that:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li>You have read and understood this disclaimer</li>
                        <li>You will not rely solely on this calculator for financial decisions</li>
                        <li>You will seek professional advice before making major financial decisions</li>
                        <li>You accept all risks associated with using this calculator</li>
                        <li>You will not hold us liable for any outcomes</li>
                    </ul>
                </section>

                <div className="bg-blue-50 p-6 rounded-lg mt-8">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Official Resources</h3>
                    <p className="text-blue-800 mb-3">For official information, consult:</p>
                    <ul className="list-disc list-inside text-blue-800 space-y-1 ml-4">
                        <li><strong>ATO:</strong> ato.gov.au (tax and super rules)</li>
                        <li><strong>Services Australia:</strong> servicesaustralia.gov.au (Age Pension)</li>
                        <li><strong>ASIC Moneysmart:</strong> moneysmart.gov.au (financial guidance)</li>
                        <li><strong>Financial Adviser:</strong> Seek a licensed, qualified professional</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

// Compliance Page Component
export const CompliancePage = () => {
    // Scroll to top when component mounts
    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Compliance & Standards</h1>
            
            <div className="prose prose-blue max-w-none">
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">Regulatory Compliance</h2>
                    <p className="text-gray-700 mb-4">
                        The Superannuation Calculator Suite has been designed with compliance and accuracy in mind:
                    </p>
                    
                    <div className="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-400">
                        <h3 className="text-lg font-semibold text-blue-900 mb-2">ASIC Regulatory Compliance</h3>
                        <p className="text-blue-800 mb-2">
                            This tool is designed in accordance with:
                        </p>
                        <ul className="list-disc list-inside text-blue-800 space-y-1 ml-4">
                            <li><strong>ASIC Regulatory Guide 244 (RG 244):</strong> Giving Information, Not Advice</li>
                            <li><strong>ASIC Regulatory Guide 36 (RG 36):</strong> Digital Advice</li>
                            <li><strong>ASIC Regulatory Guide 175 (RG 175):</strong> Licensing: Financial product advisers</li>
                        </ul>
                        <p className="text-blue-800 mt-2 font-semibold">
                            This calculator provides educational information only and does not constitute financial advice as defined under the Corporations Act 2001.
                        </p>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Data Sources</h3>
                    <p className="text-gray-700 mb-4">
                        All financial rates and thresholds are sourced from official Australian government publications:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li><strong>Australian Taxation Office (ATO):</strong> Contribution caps, tax rates, SMSF rules</li>
                        <li><strong>Services Australia:</strong> Age Pension rates, asset and income test thresholds</li>
                        <li><strong>Treasury:</strong> Transfer Balance Cap and major policy changes</li>
                        <li><strong>Financial Year:</strong> 2026-27 (effective July 1, 2026)</li>
                        <li><strong>Last Updated:</strong> July 2026 (indexed super caps and Transfer Balance Cap from 1 July 2026)</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Current Rates (2026-27)</h3>
                    <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <ul className="text-gray-700 space-y-2">
                            <li><strong>Transfer Balance Cap:</strong> $2.1 million</li>
                            <li><strong>Concessional Contributions Cap:</strong> $32,500 per year</li>
                            <li><strong>Non-Concessional Contributions Cap:</strong> $130,000 per year</li>
                            <li><strong>Superannuation Guarantee:</strong> 12%</li>
                            <li><strong>Division 293 Threshold:</strong> $250,000</li>
                            <li><strong>Age Pension (Maximum, Single):</strong> $31,178 per annum</li>
                            <li><strong>Age Pension (Maximum, Couple):</strong> $47,010 per annum combined</li>
                        </ul>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Calculation Methodology</h3>
                    <p className="text-gray-700 mb-4">
                        Our calculations follow industry-standard methodologies:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li><strong>Age Pension:</strong> Implements both assets and income tests as per Services Australia guidelines</li>
                        <li><strong>Tax Calculations:</strong> Uses current ATO tax brackets and offsets (LITO, LMITO where applicable)</li>
                        <li><strong>Monte Carlo Simulation:</strong> 10,000 runs with historical volatility data</li>
                        <li><strong>Historical Backtesting:</strong> Uses actual market data from 1928-2025</li>
                        <li><strong>Fee Calculations:</strong> Industry-standard fee structures and compounding</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">Limitations and Disclaimers</h2>
                    <p className="text-gray-700 mb-4">
                        While we maintain high standards, users should be aware of:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li><strong>Not Financial Advice:</strong> This is an educational tool, not licensed financial advice</li>
                        <li><strong>Simplifications:</strong> Some complex scenarios may be simplified for usability</li>
                        <li><strong>Rate Changes:</strong> Government rates change regularly; we update promptly but cannot guarantee real-time accuracy</li>
                        <li><strong>Personal Circumstances:</strong> Cannot account for all individual tax situations or complexities</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">Privacy & Security Standards</h2>
                    <p className="text-gray-700 mb-4">
                        We comply with Australian privacy standards:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li><strong>Privacy Act 1988:</strong> We adhere to Australian Privacy Principles (where applicable)</li>
                        <li><strong>Data Storage:</strong> All calculation data stays on your device (no server storage)</li>
                        <li><strong>No Personal Information:</strong> We do not collect names, emails, or identifiable financial data</li>
                        <li><strong>Anonymous Analytics:</strong> Only aggregated, non-identifiable usage statistics</li>
                        <li><strong>HTTPS:</strong> All connections secured with SSL/TLS encryption</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">Updates and Maintenance</h2>
                    <p className="text-gray-700 mb-4">
                        We maintain the calculator through:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li><strong>Regular Rate Updates:</strong> Following government announcements (budget, indexation)</li>
                        <li><strong>Bug Fixes:</strong> Ongoing monitoring and fixes for any calculation errors</li>
                        <li><strong>Feature Improvements:</strong> Based on user feedback and regulatory changes</li>
                        <li><strong>Security Patches:</strong> Regular updates for security and performance</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">Transparency</h2>
                    <p className="text-gray-700 mb-4">
                        We believe in transparency:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                        <li><strong>Open Methodology:</strong> Calculation methods are documented in tutorials</li>
                        <li><strong>Data Sources:</strong> All rates referenced to official government sources</li>
                        <li><strong>Assumptions:</strong> All assumptions clearly stated in calculator interfaces</li>
                        <li><strong>Limitations:</strong> Known limitations and simplifications are disclosed</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">Contact & Feedback</h2>
                    <p className="text-gray-700 mb-4">
                        We welcome feedback on accuracy and compliance:
                    </p>
                    <p className="text-gray-700">
                        <strong>Email:</strong> support@supercalcpro.com.au
                    </p>
                    <p className="text-gray-700 mt-2">
                        If you identify any errors or have concerns about calculation accuracy, please contact us immediately.
                    </p>
                </section>

                <div className="bg-green-50 p-6 rounded-lg mt-8">
                    <h3 className="text-lg font-semibold text-green-900 mb-2">Commitment to Accuracy</h3>
                    <p className="text-green-800">
                        We are committed to maintaining the highest standards of accuracy and compliance. 
                        All rates are sourced from official government publications and updated regularly. 
                        However, always verify critical information with the ATO, Services Australia, or your financial adviser.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default { PrivacyPolicy, TermsOfService, DisclaimerPage, CompliancePage };

